/**
 * Domínio: UI de Atendimento (tickets).
 *
 * Agrupa eventos de pipeline, slots de render e contextos tipados da área de
 * atendimento. Acesse via `tickets.events.ACCEPT`, `tickets.slots.LIST_ITEM_ACTIONS`.
 *
 * Os valores (string literais) são o contrato com o frontend e com os plugins —
 * não altere as strings, só adicione novas.
 */
import type { InterceptContextBase } from "../types";

export const tickets = {
  /** Eventos de pipeline (intercept before/on/after). */
  events: {
    CREATE: "create-ticket",
    ACCEPT: "accept-ticket",
    TRANSFER: "transfer-ticket",
    RESOLVE: "resolve-ticket",
    IGNORE: "ignore-ticket",
    OPEN: "open-ticket",
    SEND_MESSAGE: "send-message",
  },
  /** Slots de render (PluginBridge slot=...). */
  slots: {
    ACTIONS: "ticket-actions",
    HEADER: "ticket-header",
    SIDEBAR: "ticket-sidebar",
    MESSAGE_INPUT: "message-input",
    MESSAGE_ACTIONS: "message-actions",
    MESSAGE_CONTENT: "message-content",
    MESSAGE_FOOTER: "message-footer",
    /**
     * Card de um arquivo genérico (não-PDF) na bolha. O plugin pode renderizar
     * um card próprio aqui; para SUBSTITUIR o card nativo (em vez de somar),
     * declare também o hook MESSAGE_FILE_CARD_HIDE_NATIVE. Contexto:
     * MessageFileCardContext (filename, ext, mimetype, mediaUrl, ...).
     */
    MESSAGE_FILE_CARD: "message-file-card",
    /**
     * Faixa que separa um ticket do outro no histórico. O nativo mostra
     * "#Chamado 123 - Fila · Conexão" (#4155). O plugin pode somar informação
     * aqui; para SUBSTITUIR a faixa nativa, declare também o hook
     * MESSAGE_TICKET_SEPARATOR_HIDE_NATIVE. Contexto:
     * MessageTicketSeparatorContext.
     */
    MESSAGE_TICKET_SEPARATOR: "message-ticket-separator",
    NEW: "new-ticket",
    NEW_BUTTON: "new-ticket-button",
    LIST_ITEM_ACTIONS: "ticket-list-item-actions",
    LIST_ITEM_BADGE: "ticket-list-item-badge",
  },
  /** Hooks de decisão (registerUIHook). */
  hooks: {
    NEW_BUTTON_HIDE_NATIVE: "new-ticket-button:hide-native",
    /** Esconde o card de arquivo nativo (o plugin renderiza o seu no slot MESSAGE_FILE_CARD). */
    MESSAGE_FILE_CARD_HIDE_NATIVE: "message-file-card:hide-native",
    /** Esconde a faixa separadora nativa (o plugin renderiza a sua no slot MESSAGE_TICKET_SEPARATOR). */
    MESSAGE_TICKET_SEPARATOR_HIDE_NATIVE: "message-ticket-separator:hide-native",
  },
} as const;

/** Ticket como o frontend o conhece (subconjunto estável). */
export interface UITicket {
  id: number;
  uuid?: string;
  status?: string;
  channel?: string;
  isGroup?: boolean;
  contactId?: number;
  queueId?: number;
  userId?: number;
  [k: string]: any;
}

/** create-ticket: payload do ticket + conexão + origem (painel). */
export interface CreateTicketContext extends InterceptContextBase {
  payload: {
    contactId?: number;
    whatsappId?: number;
    userId?: number;
    status?: string;
    [k: string]: any;
  };
  connection?: { id: number; channel: string } | null;
  source?: {
    number?: string;
    contactId?: number | null;
    templateName?: string;
    [k: string]: any;
  };
}

/** accept-ticket: ticket sendo aceito pelo usuário atual. */
export interface AcceptTicketContext extends InterceptContextBase {
  ticket: UITicket;
  userId?: number;
}

/** transfer-ticket: abertura do fluxo de transferência. */
export interface TransferTicketContext extends InterceptContextBase {
  ticket: UITicket;
}

/** resolve-ticket: ticket sendo finalizado/resolvido. */
export interface ResolveTicketContext extends InterceptContextBase {
  ticket: UITicket;
  sendFarewellMessage?: boolean;
  finalizacaoMessage?: string;
}

/** ignore-ticket: ticket sendo fechado sem despedida. */
export interface IgnoreTicketContext extends InterceptContextBase {
  ticket: UITicket;
  userId?: number;
}

/** open-ticket: usuário abriu/selecionou um ticket. */
export interface OpenTicketContext extends InterceptContextBase {
  ticket: UITicket;
}

/** send-message: envio de mensagem de texto. */
export interface SendMessageContext extends InterceptContextBase {
  ticketId: number;
  message: { body: string; fromMe?: boolean; mediaUrl?: string; [k: string]: any };
}

/** Contexto dos slots de mensagem (menu, conteúdo, rodapé). */
export interface MessageActionContext {
  messageId: string;
  ticketId?: number;
  body?: string;
  fromMe?: boolean;
  mediaUrl?: string;
  /** ISO string da criação — use para mostrar data/hora custom. */
  createdAt?: string;
}

/**
 * Contexto do slot MESSAGE_FILE_CARD — um arquivo genérico (não-PDF) na bolha.
 * O plugin usa isto para renderizar um card próprio (e, com o hook
 * MESSAGE_FILE_CARD_HIDE_NATIVE, substituir o card nativo).
 */
export interface MessageFileCardContext extends MessageActionContext {
  /** Nome de exibição do arquivo (com extensão), já resolvido pelo app. */
  filename: string;
  /** Extensão em minúsculas, sem ponto (ex: "docx"); "" se não houver. */
  ext: string;
  /** mimetype do arquivo, quando conhecido. */
  mimetype?: string;
  /** wid (id externo do canal) da mensagem, quando houver. */
  wid?: string;
  /** Canal da mensagem (ex: "whatsapp_hardapi") — use para só agir nas SUAS conexões. */
  channel?: string;
  /** Id da conexão (Whatsapp) dona da mensagem. */
  whatsappId?: number;
  /** Mensagem de um ticket de grupo. */
  isGroup?: boolean;
}

/**
 * Contexto do slot MESSAGE_TICKET_SEPARATOR — a faixa que separa um ticket do
 * outro no histórico (#4155). O plugin usa isto para somar informação à faixa
 * ou, com o hook MESSAGE_TICKET_SEPARATOR_HIDE_NATIVE, substituí-la.
 *
 * Traz o ticket que COMEÇA abaixo da faixa e o que terminou acima, para o
 * plugin poder reagir à transição (ex: destacar quando a conexão muda).
 */
export interface MessageTicketSeparatorContext {
  /** Ticket que começa abaixo desta faixa. */
  ticketId: number;
  /** Ticket imediatamente anterior no histórico (o que terminou acima). */
  previousTicketId?: number;
  /** Fila do ticket que começa; ausente quando o ticket não tem fila. */
  queue?: { id: number; name: string; color?: string };
  /**
   * Conexão do ticket que começa (#4155) — id, nome e o identificador da
   * conexão (`number`), que é o que de fato separa os atendimentos.
   */
  connection?: { id: number; name?: string; number?: string };
  /** Conexão do ticket anterior. Difere de `connection` na virada de conexão. */
  previousConnection?: { id: number; name?: string; number?: string };
  /** true quando a faixa marca troca de CONEXÃO, não só de ticket. */
  isConnectionChange: boolean;
  /** Canal do ticket que começa (ex: "whatsapp_hardapi"). */
  channel?: string;
  /** Ticket de grupo. */
  isGroup?: boolean;
}
